import React from 'react';
import { ArrowLeft, Download, ImagePlus, Loader2, Play, Sparkles, X } from 'lucide-react';
import {
  useCaseBySlug,
  useCases,
  useCaseNeedsImage,
  type UseCaseDef,
} from './useCasesData';

interface ResultItem {
  kind: 'image' | 'audio' | 'video';
  url: string;
}

function extractMediaURL(obj: unknown, exts: string[]): string {
  const seen = new Set<unknown>();
  const walk = (v: unknown): string => {
    if (!v || seen.has(v)) return '';
    if (typeof v === 'string') {
      const low = v.split('?')[0].toLowerCase();
      if ((v.startsWith('http') || v.startsWith('data:')) && exts.some((e) => low.endsWith(e) || v.startsWith(`data:${e}`)))
        return v;
      return '';
    }
    if (typeof v !== 'object') return '';
    seen.add(v);
    for (const val of Object.values(v as Record<string, unknown>)) {
      const found = walk(val);
      if (found) return found;
    }
    return '';
  };
  return walk(obj);
}

async function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
}

const STEPS: Record<string, [string, string, string]> = {
  image: ['Upload your photo', 'Pick a style or describe what you want', 'Generate and download the result'],
  gen: ['Describe what you want', 'Generate — the gateway routes to a model', 'Download and reuse anywhere'],
  audio: ['Paste your text or describe the sound', 'Generate the audio', 'Play, download, and drop it in your editor'],
  video: ['Describe the shot (and upload a photo if animating one)', 'Generate a short clip', 'Download the video'],
};

function stepsFor(u: UseCaseDef): [string, string, string] {
  if (u.kind === 'tts' || u.kind === 'music' || u.kind === 'sfx') return STEPS.audio;
  if (u.kind === 'video' || u.kind === 'image-video') return STEPS.video;
  if (u.kind === 'image-gen') return STEPS.gen;
  return STEPS.image;
}

export const UseCasePage: React.FC<{ slug: string }> = ({ slug }) => {
  const def = useCaseBySlug[slug];

  const [input, setInput] = React.useState(def?.defaultInput ?? '');
  const [image, setImage] = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState('');
  const [results, setResults] = React.useState<ResultItem[]>([]);
  const fileRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setInput(def?.defaultInput ?? '');
    setImage('');
    setResults([]);
    setError('');
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!def) {
    return (
      <main className="px-4 pb-24 pt-28">
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-2xl font-bold text-white">Tool not found</h1>
          <a href="/ai" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-400 hover:underline">
            <ArrowLeft size={15} /> All AI tools
          </a>
        </div>
      </main>
    );
  }

  const needsImage = useCaseNeedsImage(def.kind);
  const needsText = def.kind !== 'remove-bg';
  const related = useCases.filter((u) => u.category === def.category && u.slug !== def.slug).slice(0, 6);
  const steps = stepsFor(def);

  async function onPickFile(files: FileList | null) {
    const f = files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) {
      setError('Image too large — max 12MB.');
      return;
    }
    setError('');
    setImage(await fileToDataURL(f));
  }

  async function run() {
    if (needsImage && !image) {
      setError('Upload an image first.');
      return;
    }
    if (needsText && !input.trim() && def.kind !== 'image-edit') {
      setError('Enter some text first.');
      return;
    }
    setRunning(true);
    setError('');
    try {
      const finalPrompt = def.prompt ? def.prompt.replace('{input}', input.trim() || def.defaultInput || '') : input.trim();
      let out: ResultItem[] = [];
      const post = async (url: string, body: unknown) => {
        const resp = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (resp.status === 401) throw new Error('Sign in to run this tool — it uses your credits. Sign in at /login and retry.');
        return resp;
      };

      if (def.kind === 'image-edit') {
        const resp = await post('/api/image/edit', { prompt: finalPrompt, image, n: def.n ?? 1 });
        const data = await resp.json();
        if (!resp.ok || !data?.success) throw new Error(data?.error || 'Generation failed');
        out = (data.images as string[]).map((u) => ({ kind: 'image', url: u }));
      } else if (def.kind === 'remove-bg') {
        const resp = await post('/api/image/remove-background', { image });
        const data = await resp.json();
        if (!resp.ok || !data?.success) throw new Error(data?.error || 'Background removal failed');
        out = [{ kind: 'image', url: data.image }];
      } else if (def.kind === 'image-gen') {
        const resp = await post('/v1/images/generations', {
          model: def.model ?? '',
          prompt: finalPrompt,
          size: def.size ?? '1024x1024',
          n: def.n ?? 1,
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error?.message || data?.error || 'Generation failed');
        out = (data?.data ?? [])
          .map((d: { url?: string; b64_json?: string }) =>
            d.url ? d.url : d.b64_json ? `data:image/png;base64,${d.b64_json}` : '',
          )
          .filter(Boolean)
          .map((u: string) => ({ kind: 'image' as const, url: u }));
      } else if (def.kind === 'tts') {
        const resp = await post('/v1/audio/speech', { input: input.trim(), voice: def.voice });
        if (!resp.ok) {
          const data = await resp.json().catch(() => null);
          throw new Error((data as { error?: { message?: string } })?.error?.message || 'Speech generation failed');
        }
        const blob = await resp.blob();
        out = [{ kind: 'audio', url: URL.createObjectURL(blob) }];
      } else if (def.kind === 'music' || def.kind === 'sfx') {
        const resp = await post('/api/audio/generate', {
          prompt: finalPrompt || input.trim(),
          kind: def.kind === 'music' ? 'music' : 'sfx',
          duration: def.duration,
        });
        const data = await resp.json();
        if (!resp.ok || !data?.item?.url) throw new Error(data?.error || 'Audio generation failed');
        out = [{ kind: 'audio', url: data.item.url }];
      } else {
        const body: Record<string, unknown> = { model: def.model ?? 'auto-video', prompt: finalPrompt };
        if (def.kind === 'image-video') body.image_url = image;
        const resp = await post('/v1/videos/generations', body);
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error?.message || data?.error || 'Video generation failed');
        const url = extractMediaURL(data, ['.mp4', '.webm', 'video/']);
        if (!url) throw new Error('Video generation returned no video');
        out = [{ kind: 'video', url }];
      }
      setResults(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setRunning(false);
    }
  }

  return (
    <main className="px-4 pb-24 pt-28 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <a href="/ai" className="inline-flex items-center gap-1.5 text-sm font-medium text-white/45 hover:text-white">
          <ArrowLeft size={15} /> All AI tools
        </a>

        <header className="mt-4 mb-8 max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-mono text-white/45">
            <Sparkles size={13} /> {def.category} · usage-based credits
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">{def.title}</h1>
          <p className="mt-3 text-base leading-relaxed text-white/55">{def.description}</p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[400px_minmax(0,1fr)]">
          <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            {needsImage ? (
              <div className="mb-4">
                <span className="mb-1.5 block text-[10px] font-mono uppercase tracking-wider text-white/40">Your image</span>
                {image ? (
                  <div className="relative">
                    <img src={image} alt="input" className="max-h-56 w-full rounded-lg border border-white/10 object-contain" />
                    <button
                      onClick={() => setImage('')}
                      className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white/70 hover:text-white"
                      aria-label="Remove image"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      void onPickFile(e.dataTransfer.files);
                    }}
                    className="flex h-36 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-black/30 text-white/40 hover:border-white/40 hover:text-white/70"
                  >
                    <ImagePlus size={22} />
                    <span className="text-xs">Click or drop an image (max 12MB)</span>
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => void onPickFile(e.target.files)} />
              </div>
            ) : null}

            {needsText ? (
              <label className="mb-4 block">
                <span className="mb-1.5 block text-[10px] font-mono uppercase tracking-wider text-white/40">
                  {def.inputLabel ?? 'Prompt'}
                </span>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={def.inputPlaceholder ?? def.defaultInput}
                  rows={def.kind === 'tts' ? 6 : 3}
                  className="w-full resize-y rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder:text-white/25 focus:border-white/30 focus:outline-none"
                />
              </label>
            ) : null}

            {def.presets?.length ? (
              <div className="mb-4 flex flex-wrap gap-1.5">
                {def.presets.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => setInput(p.input)}
                    className={`rounded-full border px-2.5 py-1 text-xs transition ${
                      input === p.input
                        ? 'border-sky-400/50 bg-sky-400/10 text-sky-300'
                        : 'border-white/10 text-white/50 hover:border-white/25 hover:text-white'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            ) : null}

            <button
              onClick={() => void run()}
              disabled={running}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-400 disabled:opacity-50"
            >
              {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={15} />}
              {running ? 'Generating…' : 'Generate'}
            </button>
            {def.kind === 'video' || def.kind === 'image-video' ? (
              <p className="mt-2 text-center text-[11px] text-white/35">Video generation can take a minute or two.</p>
            ) : null}
            {error ? (
              <p className="mt-3 rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-300">
                {error}{' '}
                {error.includes('Sign in') ? (
                  <a href={`/login?next=${encodeURIComponent(`/ai/${slug}`)}`} className="font-semibold underline">
                    Sign in
                  </a>
                ) : null}
              </p>
            ) : null}
          </section>

          <section className="min-h-[280px] rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <h2 className="mb-4 font-mono text-sm font-bold uppercase tracking-wider text-white/70">Result</h2>
            {results.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-sm text-white/30">
                {running ? 'Working…' : 'Your result will appear here.'}
              </div>
            ) : (
              <div className="grid gap-4">
                {results.map((r, i) =>
                  r.kind === 'image' ? (
                    <div key={i} className="relative">
                      <img src={r.url} alt={`${def.title} result`} className="w-full rounded-lg border border-white/10" />
                      <a
                        href={r.url}
                        download={`${def.slug}.png`}
                        className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white/80 hover:text-white"
                        aria-label="Download"
                      >
                        <Download size={15} />
                      </a>
                    </div>
                  ) : r.kind === 'audio' ? (
                    <div key={i} className="flex items-center gap-3">
                      <audio src={r.url} controls className="w-full" />
                      <a href={r.url} download={`${def.slug}.mp3`} className="text-white/60 hover:text-white" aria-label="Download">
                        <Download size={16} />
                      </a>
                    </div>
                  ) : (
                    <div key={i}>
                      <video src={r.url} controls className="w-full rounded-lg border border-white/10" />
                      <a href={r.url} download className="mt-2 inline-flex items-center gap-1.5 text-xs text-sky-400 hover:underline">
                        <Download size={13} /> Download video
                      </a>
                    </div>
                  ),
                )}
              </div>
            )}
          </section>
        </div>

        <section className="mt-12 grid gap-6 sm:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
              <span className="font-mono text-xs text-sky-400">Step {i + 1}</span>
              <p className="mt-1.5 text-sm text-white/70">{s}</p>
            </div>
          ))}
        </section>

        {related.length ? (
          <section className="mt-12">
            <h2 className="mb-4 text-lg font-semibold text-white">More {def.category} tools</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((u) => (
                <a
                  key={u.slug}
                  href={`/ai/${u.slug}`}
                  className="rounded-lg border border-white/10 bg-white/[0.02] p-4 transition hover:border-white/25"
                >
                  <span className="text-sm font-semibold text-white">{u.title}</span>
                  <p className="mt-1 text-xs text-white/45">{u.tagline}</p>
                </a>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
};
